import logError from 'App/babel/helpers/logError';

// Reminder toasts only exist in redux, and pusher never re-delivers a reminder — without
// this store a page reload silently discards every pending reminder. sessionStorage keeps
// them per tab, matching how the pusher toasts already behave.
const STORAGE_KEY = 'pendingReminders';

export type ReminderPayload = {
	entity: string;
	date?: string;
	idArray?: Array<number | string>;
	object?: { id: number | string; date?: string };
};

export type StoredReminder = {
	key: string;
	data: ReminderPayload;
	// Epoch ms for when the reminder should be shown again (set by snooze). 0 = show now.
	showAt: number;
};

const readAll = (): StoredReminder[] => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch (e) {
		logError(e, 'Failed to read pending reminders');
		return [];
	}
};

const writeAll = (reminders: StoredReminder[]) => {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(reminders));
	} catch (e) {
		logError(e, 'Failed to persist pending reminders');
	}
};

// Stable identity for a reminder across snoozes and reloads. Compute it once, when the
// pusher event arrives — pushReminder later shrinks idArray to the still-valid ids, so
// recomputing it would change the key and orphan the stored entry.
export const getReminderKey = (data: ReminderPayload): string => {
	const ids = data.idArray ? [...data.idArray].sort().join(',') : String(data.object?.id ?? '');
	return `${data.entity}:${ids}:${data.date || data.object?.date || ''}`;
};

export const getPendingReminders = (): StoredReminder[] => readAll();

export const saveReminder = (key: string, data: ReminderPayload, showAt = 0): void => {
	const others = readAll().filter(reminder => reminder.key !== key);
	writeAll([...others, { key, data, showAt }]);
};

export const removeReminder = (key: string): void => {
	const reminders = readAll();
	const remaining = reminders.filter(reminder => reminder.key !== key);
	if (remaining.length !== reminders.length) {
		writeAll(remaining);
	}
};
